import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useAuth } from '../../hooks/useApi';
import { useTenantContext } from '../../contexts/TenantContext';
import { apiClient } from '../../lib/api';
import StripePayment from './StripePayment';
import { X } from 'lucide-react';

interface Plan {
  plan_id: string;
  name: string;
  billing_cycle?: 'monthly' | 'annual';
  price: number;
  annual_price?: number;
  annual_savings?: number;
  sms_limit: number;
  stripe_product_id?: string;
  features: string[];
}

interface Addon {
  addon_id: string;
  name: string;
  price: number;
  billing_cycle?: 'monthly' | 'one_time';
  stripe_product_id?: string;
  credits?: number;
  description: string;
}

interface Subscription {
  tenant_id: string;
  current_plan: string;
  plan_info: Plan;
  addons: Record<string, boolean>;
  billing_cycle: string;
  subscription_status: string;
  billing_status?: string;
  service_access?: string;
  cancellation_effective_date?: string | number;
  is_suspended: boolean;
  is_trial: boolean;
  trial_end_date?: string;
  next_billing_date?: string;
  grace_period_end?: number;
  sms_limit_restricted?: boolean;
}

interface BillingHistoryItem {
  record_id: string;
  change_type: string;
  old_plan?: string;
  new_plan?: string;
  addon_id?: string;
  enabled?: boolean;
  bundle_size?: number;
  price?: number;
  amount?: number;
  currency?: string;
  transaction_id?: string;
  stripe_payment_intent?: string;
  receipt_url?: string;
  changed_by: string;
  changed_at: number;
  effective_date?: number;
  payment_reference?: string;
}

export default function Billing() {
  const { user } = useAuth();
  const { selectedTenantId } = useTenantContext();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [availablePlans, setAvailablePlans] = useState<Plan[]>([]);
  const [availableAddons, setAvailableAddons] = useState<Addon[]>([]);
  const [billingHistory, setBillingHistory] = useState<BillingHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [upgrading] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [showPayment, setShowPayment] = useState<{plan: string, name: string, price: number} | null>(null);
  const [activeTab, setActiveTab] = useState('subscription');
  const [isSandbox, setIsSandbox] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');

  useEffect(() => {
    if (selectedTenantId) {
      loadBillingData();
    }
  }, [selectedTenantId]);

  const loadBillingData = async () => {
    if (!selectedTenantId) return;
    
    try {
      setLoading(true);
      
      // Load subscription details
      const subscription = await apiClient.getSubscription(selectedTenantId);
      setSubscription(subscription as Subscription);

      // Load available plans
      const plansData = await apiClient.getPlans();
      setAvailablePlans(plansData.plans);

      // Load available addons
      const addonsData = await apiClient.getAddons();
      setAvailableAddons(addonsData.addons);

      // Load billing history
      const historyData = await apiClient.getBillingHistory(selectedTenantId);
      setBillingHistory(historyData.history);
      
      // Detect sandbox mode from Stripe key
      const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';
      setIsSandbox(stripeKey.startsWith('pk_test_'));

    } catch (error) {
      console.error('Failed to load billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const changePlan = async (newPlan: string) => {
    if (!selectedTenantId) return;
    
    if (subscription?.is_trial) {
      // For trial users, initiate Stripe payment
      await upgradeFromTrial(newPlan);
    } else {
      // For existing paid users, change plan directly
      try {
        await apiClient.changePlan(selectedTenantId, newPlan, user?.email || 'unknown');
        await loadBillingData(); // Reload data
        setActiveTab('subscription'); // Navigate back to Current Plan tab
      } catch (error) {
        console.error('Failed to change plan:', error);
      }
    }
  };

  const upgradeFromTrial = async (newPlan: string) => {
    const selectedPlan = availablePlans.find(p => p.plan_id === newPlan);
    
    if (selectedPlan) {
      const price = selectedPlan.billing_cycle === 'annual' && selectedPlan.annual_price 
        ? selectedPlan.annual_price 
        : selectedPlan.price;
        
      setShowPayment({
        plan: newPlan,
        name: `${selectedPlan.name} ${selectedPlan.billing_cycle === 'annual' ? 'Annual' : 'Monthly'}`,
        price: price
      });
    }
  };

  const handlePaymentSuccess = async () => {
    setShowPayment(null);
    await loadBillingData(); // Reload data after successful payment
    setActiveTab('subscription'); // Navigate back to Current Plan tab
  };

  const handlePaymentCancel = () => {
    setShowPayment(null);
  };

  const toggleAddon = async (addonId: string, enabled: boolean) => {
    if (!selectedTenantId) return;
    
    // If enabling a monthly addon, show payment modal first
    if (enabled) {
      const addon = availableAddons.find(a => a.addon_id === addonId);
      if (addon && addon.billing_cycle === 'monthly') {
        setShowPayment({
          plan: addonId,
          name: addon.name,
          price: addon.price
        });
        return;
      }
    }
    
    // For disabling or one-time purchases, proceed directly
    try {
      await apiClient.toggleAddon(selectedTenantId, addonId, enabled, user?.email || 'unknown');
      await loadBillingData(); // Reload data
      setActiveTab('subscription'); // Navigate back to Current Plan tab
    } catch (error) {
      console.error('Failed to toggle addon:', error);
    }
  };

  const cancelSubscription = async () => {
    if (!selectedTenantId) return;
    
    const isTrialAccount = subscription?.is_trial;
    const message = isTrialAccount 
      ? 'Are you sure you want to cancel your trial? This will immediately disable your service.'
      : 'Are you sure you want to cancel your subscription? Your service will remain active until the end of your current billing period.';
    
    if (!confirm(message)) {
      return;
    }
    
    try {
      setCancelling(true);
      const result = await apiClient.cancelSubscription(selectedTenantId, user?.email || 'unknown') as any;
      await loadBillingData(); // Reload data
      setActiveTab('subscription'); // Navigate back to Current Plan tab
      
      if (result.immediate || isTrialAccount) {
        alert('Subscription cancelled immediately.');
      } else if (result.effective_date) {
        const effectiveDate = new Date(result.effective_date * 1000).toLocaleDateString();
        alert(`Subscription will be cancelled at the end of your billing period (${effectiveDate}). You can still use the service until then.`);
      }
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      alert('Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return <div className="p-6">Loading billing information...</div>;
  }

  if (showPayment && selectedTenantId) {
    return (
      <div className="p-6">
        <StripePayment
          tenantId={selectedTenantId}
          plan={showPayment.plan}
          planName={showPayment.name}
          amount={showPayment.price}
          onSuccess={handlePaymentSuccess}
          onCancel={handlePaymentCancel}
        />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {isSandbox && (
        <div className="bg-orange-100 border-l-4 border-orange-500 p-4 mb-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-orange-700">
                <strong>SANDBOX MODE:</strong> You are using test payment data. No real charges will be made.
              </p>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Billing & Subscription</h1>
        {subscription?.is_trial && (
          <Badge variant="secondary">Trial Account</Badge>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="subscription">Current Plan</TabsTrigger>
          <TabsTrigger value="plans">Change Plan</TabsTrigger>
          <TabsTrigger value="addons">Add-ons</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="history">Transaction History</TabsTrigger>
        </TabsList>

        <TabsContent value="subscription">
          {subscription && (
            <div className="space-y-4">
              {subscription.is_trial && (
                <Card className="border-orange-200 bg-orange-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-orange-800">
                      🎯 14-Day Free Trial
                      <Badge variant="secondary" className="bg-orange-200 text-orange-800">
                        Trial Active
                      </Badge>
                    </CardTitle>
                    <CardDescription className="text-orange-700">
                      {subscription.trial_end_date && (
                        <>Trial expires on {new Date(subscription.trial_end_date).toLocaleDateString()}</>
                      )}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-white p-4 rounded-lg border border-orange-200">
                      <h4 className="font-medium text-orange-800 mb-2">Trial Includes:</h4>
                      <ul className="text-sm space-y-1 text-orange-700">
                        <li className="flex items-center gap-2">
                          <span className="text-orange-500">✓</span>
                          Core queue management features
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-orange-500">✓</span>
                          1,000 SMS notifications included
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-orange-500">✓</span>
                          Basic analytics and reporting
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-orange-500">✓</span>
                          Email support
                        </li>
                      </ul>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <h4 className="font-medium text-blue-800 mb-2">Ready to upgrade?</h4>
                      <p className="text-sm text-blue-700 mb-3">
                        Choose a plan below to continue with full features after your trial ends.
                      </p>
                      <Button 
                        onClick={() => setActiveTab('plans')}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        View Plans & Upgrade
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    Current {subscription.is_trial ? 'Trial' : 'Subscription'}
                    <Badge variant={
                      subscription.subscription_status === 'active' && subscription.billing_status !== 'overdue' ? 'default' :
                      subscription.subscription_status === 'past_due' || subscription.billing_status === 'payment_failed' ? 'secondary' :
                      subscription.subscription_status === 'incomplete' || subscription.billing_status === 'action_required' ? 'secondary' :
                      subscription.billing_status === 'overdue' || subscription.service_access === 'suspended' ? 'destructive' :
                      'default'
                    }>
                      {subscription.billing_status === 'overdue' ? 'Service Suspended' :
                       subscription.billing_status === 'payment_failed' ? 'Grace Period' :
                       subscription.billing_status === 'action_required' ? 'Action Required' :
                       subscription.subscription_status === 'past_due' ? 'Payment Failed' :
                       subscription.subscription_status === 'incomplete' ? 'Action Required' :
                       subscription.subscription_status}
                    </Badge>
                  </CardTitle>
                  <CardDescription>
                    Your current plan and usage details
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h3 className="font-semibold text-lg">
                        {subscription.is_trial ? 'Trial Plan' : subscription.plan_info.name}
                      </h3>
                      <p className="text-2xl font-bold">
                        {subscription.is_trial ? 'Free' : `$${subscription.plan_info.price}/month`}
                      </p>
                      <p className="text-sm text-gray-600">
                        {subscription.sms_limit_restricted ? (
                          <>200 SMS messages (grace period limit)</>
                        ) : subscription.service_access === 'suspended' ? (
                          <>SMS service suspended</>
                        ) : (
                          <>{subscription.plan_info.sms_limit} SMS messages included</>
                        )}
                      </p>
                      {subscription.sms_limit_restricted && (
                        <div className="mt-1 p-2 bg-yellow-50 border border-yellow-200 rounded">
                          <p className="text-xs text-yellow-700">
                            📱 SMS limit reduced to 200/month during grace period
                          </p>
                        </div>
                      )}
                      {subscription.subscription_status === 'cancel_at_period_end' && subscription.cancellation_effective_date && (
                        <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded">
                          <p className="text-sm text-orange-700 font-medium">
                            ⚠️ Subscription will end on {new Date(Number(subscription.cancellation_effective_date) * 1000).toLocaleDateString()}
                          </p>
                          <p className="text-xs text-orange-600">
                            You can still use all features until then.
                          </p>
                        </div>
                      )}
                      {subscription.subscription_status === 'past_due' && (
                        <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded">
                          <p className="text-sm text-orange-700 font-medium">
                            ⚠️ Payment Failed - Grace Period Active
                          </p>
                          <p className="text-xs text-orange-600">
                            {subscription.grace_period_end ? (
                              <>Grace period ends {new Date(subscription.grace_period_end * 1000).toLocaleDateString()}. SMS limited to 200/month during grace period.</>
                            ) : (
                              'SMS limited to 200/month until payment is resolved.'
                            )}
                          </p>
                        </div>
                      )}
                      {subscription.billing_status === 'overdue' && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                          <p className="text-sm text-red-700 font-medium">
                            ❌ Service Suspended - Payment Overdue
                          </p>
                          <p className="text-xs text-red-600">
                            Please update your payment method to restore service.
                          </p>
                        </div>
                      )}
                      {subscription.subscription_status === 'incomplete' && (
                        <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                          <p className="text-sm text-yellow-700 font-medium">
                            ⚠️ Payment Action Required
                          </p>
                          <p className="text-xs text-yellow-600">
                            Please complete payment verification (3D Secure, etc.)
                          </p>
                        </div>
                      )}
                      {subscription.next_billing_date && !subscription.is_trial && subscription.subscription_status === 'active' && (
                        <p className="text-sm text-gray-600">
                          Next billing: {new Date(subscription.next_billing_date).toLocaleDateString()}
                        </p>
                      )}
                      {(subscription.subscription_status === 'active' || subscription.subscription_status === 'cancel_at_period_end') && !subscription.is_trial && (
                        <div className="mt-3 space-y-2">
                          <div className="flex gap-2">
                            {subscription.current_plan.includes('professional') && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => changePlan('premium_monthly')}
                                  className="border-blue-300 text-blue-600 hover:bg-blue-50"
                                >
                                  Upgrade to Premium
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => changePlan('enterprise_monthly')}
                                  className="border-purple-300 text-purple-600 hover:bg-purple-50"
                                >
                                  Upgrade to Enterprise
                                </Button>
                              </>
                            )}
                            {subscription.current_plan.includes('premium') && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => changePlan('enterprise_monthly')}
                                className="border-purple-300 text-purple-600 hover:bg-purple-50"
                              >
                                Upgrade to Enterprise
                              </Button>
                            )}
                          </div>
                          {subscription.subscription_status === 'active' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={cancelSubscription}
                              disabled={cancelling}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                            >
                              <X className="h-4 w-4 mr-1" />
                              {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
                            </Button>
                          )}
                          {subscription.subscription_status === 'cancel_at_period_end' && (
                            <div className="text-sm text-orange-600">
                              Subscription scheduled for cancellation
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <h4 className="font-medium mb-2">Features:</h4>
                      <ul className="text-sm space-y-1">
                        {subscription.plan_info.features.map((feature, index) => (
                          <li key={index} className="flex items-center gap-2">
                            <span className="text-green-500">✓</span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="plans">
          {availablePlans.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <div className="text-gray-400 mb-4">
                  <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Plans Loading...</h3>
                <p className="text-sm text-gray-500 mb-4">If plans don't appear, please refresh the page or contact support.</p>
                <Button onClick={loadBillingData} variant="outline">
                  Retry Loading Plans
                </Button>
              </CardContent>
            </Card>
          ) : (
          <div className="space-y-8">
            {/* Billing Cycle Toggle */}
            <div className="flex justify-center">
              <div className="inline-flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-6 py-2 rounded-md font-medium transition-all ${
                    billingCycle === 'monthly'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle('annual')}
                  className={`px-6 py-2 rounded-md font-medium transition-all flex items-center gap-2 ${
                    billingCycle === 'annual'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Annual
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">
                    Save 20%
                  </span>
                </button>
              </div>
            </div>

            {/* Plan Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {['Professional', 'Premium', 'Enterprise'].map((planName) => {
                const monthlyPlan = availablePlans.find(p => p.name === planName && (p.billing_cycle === 'monthly' || !p.billing_cycle));
                const annualPlan = availablePlans.find(p => p.name === planName && p.billing_cycle === 'annual');
                const selectedPlan = billingCycle === 'annual' && annualPlan ? annualPlan : monthlyPlan;
                
                if (!selectedPlan) return null;
                
                const isCurrentPlan = subscription?.current_plan === selectedPlan.plan_id;
                const isPremium = planName === 'Premium';
                
                // Calculate display price (monthly equivalent for annual plans)
                const displayPrice = selectedPlan.price;
                const annualTotal = billingCycle === 'annual' && selectedPlan.annual_price ? selectedPlan.annual_price : null;
                const savings = billingCycle === 'annual' && selectedPlan.annual_savings ? selectedPlan.annual_savings : 0;
                
                return (
                  <Card key={planName} className={`relative ${
                    isCurrentPlan ? 'ring-2 ring-blue-500' : ''
                  } ${
                    isPremium ? 'border-2 border-blue-500 shadow-lg' : ''
                  }`}>
                    {isPremium && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <Badge className="bg-blue-500 text-white px-4 py-1">Most Popular</Badge>
                      </div>
                    )}
                    {isCurrentPlan && (
                      <div className="absolute -top-3 right-4">
                        <Badge className="bg-green-500 text-white">Current Plan</Badge>
                      </div>
                    )}
                    
                    <CardHeader className="text-center pb-4">
                      <CardTitle className="text-2xl">{planName}</CardTitle>
                      <div className="mt-4">
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-4xl font-bold">${displayPrice}</span>
                          <span className="text-gray-600">/month</span>
                        </div>

                        {annualTotal && (
                          <p className="text-sm text-gray-500 mt-1">
                            Billed annually (${annualTotal})
                          </p>
                        )}
                        {savings > 0 && billingCycle === 'annual' && (
                          <div className="mt-2">
                            <Badge className="bg-green-100 text-green-700 font-semibold">
                              💰 Save ${savings}/year
                            </Badge>
                          </div>
                        )}
                      </div>
                      <CardDescription className="mt-2">
                        {selectedPlan.sms_limit.toLocaleString()} SMS messages/month
                      </CardDescription>
                    </CardHeader>
                    
                    <CardContent className="space-y-4">
                      {!isCurrentPlan && (
                        <Button 
                          onClick={() => changePlan(selectedPlan.plan_id)}
                          className={`w-full ${
                            isPremium 
                              ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800' 
                              : ''
                          }`}
                          disabled={upgrading === selectedPlan.plan_id}
                        >
                          {upgrading === selectedPlan.plan_id ? (
                            'Processing...'
                          ) : subscription?.is_trial ? (
                            `Start with ${planName}`
                          ) : (
                            `Switch to ${planName}`
                          )}
                        </Button>
                      )}
                      
                      <div className="border-t pt-4">
                        <ul className="space-y-2 text-sm">
                          {selectedPlan.features.map((feature, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <span className="text-green-500 mt-0.5">✓</span>
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Feature Comparison Table */}
            <div className="mt-12">
              <h3 className="text-2xl font-bold text-center mb-6">Compare Plans</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2">
                      <th className="text-left p-4 font-semibold">Features</th>
                      <th className="text-center p-4 font-semibold">Professional</th>
                      <th className="text-center p-4 font-semibold bg-blue-50">Premium</th>
                      <th className="text-center p-4 font-semibold">Enterprise</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="p-4">Multi-location Support</td>
                      <td className="text-center p-4">Up to 3</td>
                      <td className="text-center p-4 bg-blue-50">Up to 10</td>
                      <td className="text-center p-4">Unlimited</td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-4">Concurrent Queues</td>
                      <td className="text-center p-4">5</td>
                      <td className="text-center p-4 bg-blue-50">25</td>
                      <td className="text-center p-4">Unlimited</td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-4">AI SMS Assistant</td>
                      <td className="text-center p-4 text-gray-400">—</td>
                      <td className="text-center p-4 bg-blue-50 text-green-600">✓</td>
                      <td className="text-center p-4 text-green-600">✓</td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-4">Natural Language Processing</td>
                      <td className="text-center p-4 text-gray-400">—</td>
                      <td className="text-center p-4 bg-blue-50 text-green-600">✓</td>
                      <td className="text-center p-4 text-green-600">✓</td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-4">Advanced Reports</td>
                      <td className="text-center p-4 text-gray-400">—</td>
                      <td className="text-center p-4 bg-blue-50 text-green-600">✓</td>
                      <td className="text-center p-4 text-green-600">✓</td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-4">White Labeling</td>
                      <td className="text-center p-4 text-gray-400">—</td>
                      <td className="text-center p-4 bg-blue-50 text-gray-400">—</td>
                      <td className="text-center p-4 text-green-600">✓</td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-4">API Access</td>
                      <td className="text-center p-4 text-gray-400">—</td>
                      <td className="text-center p-4 bg-blue-50 text-green-600">✓</td>
                      <td className="text-center p-4">API + Webhooks</td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-4">Priority Support</td>
                      <td className="text-center p-4">Email</td>
                      <td className="text-center p-4 bg-blue-50">Email + Chat</td>
                      <td className="text-center p-4">Phone + Chat</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          )}
        </TabsContent>

        <TabsContent value="addons">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {availableAddons.map((addon) => {
                const isActive = subscription?.addons[addon.addon_id];
                const isMonthly = addon.billing_cycle === 'monthly';
                
                return (
                  <Card key={addon.addon_id} className={isActive ? 'ring-2 ring-green-500' : ''}>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        {addon.name}
                        {isActive && (
                          <Badge className="bg-green-500 text-white">Active</Badge>
                        )}
                      </CardTitle>
                      <CardDescription>{addon.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-bold">${addon.price.toFixed(2)}</span>
                          <span className="text-gray-600">/{isMonthly ? 'month' : 'one-time'}</span>
                        </div>
                        {addon.credits && (
                          <p className="text-sm text-gray-500 mt-1">
                            {addon.credits.toLocaleString()} credits • ${(addon.price / addon.credits * 1000).toFixed(2)} per 1,000
                          </p>
                        )}
                      </div>
                      
                      {isMonthly ? (
                        <Button
                          variant={isActive ? "outline" : "default"}
                          className={`w-full ${isActive ? 'border-red-300 text-red-600 hover:bg-red-50' : ''}`}
                          onClick={() => toggleAddon(addon.addon_id, !isActive)}
                        >
                          {isActive ? 'Remove Add-on' : 'Add to Plan'}
                        </Button>
                      ) : (
                        <Button className="w-full" onClick={() => alert('SMS credit purchase coming soon!')}>
                          Purchase Credits
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            
            {availableAddons.length === 0 && (
              <Card>
                <CardContent className="p-12 text-center">
                  <div className="text-gray-400 mb-2">
                    <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <p className="text-gray-500">No add-ons available</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="invoices">
          <div className="space-y-3">
            {billingHistory.filter(item => item.receipt_url).map((item) => {
              const formatTitle = () => {
                if (item.change_type === 'plan_change') return item.new_plan?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Plan';
                if (item.change_type === 'addon_change') return item.addon_id?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Add-on';
                if (item.change_type === 'sms_purchase') return `${item.bundle_size?.toLocaleString()} SMS Credits`;
                return 'Payment';
              };
              
              return (
                <Card key={item.record_id}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-semibold">{formatTitle()}</h4>
                          <Badge variant="outline">{item.change_type.replace('_', ' ')}</Badge>
                        </div>
                        <p className="text-sm text-gray-600">
                          {new Date(item.changed_at * 1000).toLocaleDateString('en-US', { 
                            year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </p>
                        {item.transaction_id && (
                          <p className="text-xs text-gray-400 mt-1 font-mono">ID: {item.transaction_id}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-2xl font-bold">${(item.amount || item.price)?.toFixed(2)}</p>
                          <p className="text-xs text-gray-500">{item.currency || 'USD'}</p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => window.open(item.receipt_url, '_blank')}
                        >
                          Download
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {billingHistory.filter(item => item.receipt_url).length === 0 && (
              <Card>
                <CardContent className="p-12 text-center">
                  <div className="text-gray-400 mb-2">
                    <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <p className="text-gray-500">No invoices yet</p>
                  <p className="text-sm text-gray-400 mt-1">Receipts will appear after your first payment</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="history">
          <div className="space-y-2">
            {billingHistory.map((item) => {
              const getIcon = () => {
                if (item.change_type === 'plan_change') return '📦';
                if (item.change_type === 'addon_change') return item.enabled ? '➕' : '➖';
                if (item.change_type === 'sms_purchase') return '💬';
                if (item.change_type === 'subscription_cancelled') return '❌';
                return '📝';
              };
              
              const getDescription = () => {
                if (item.change_type === 'plan_change') {
                  return `${item.old_plan?.replace(/_/g, ' ')} → ${item.new_plan?.replace(/_/g, ' ')}`;
                }
                if (item.change_type === 'addon_change') {
                  return `${item.enabled ? 'Added' : 'Removed'} ${item.addon_id?.replace(/_/g, ' ')}`;
                }
                if (item.change_type === 'sms_purchase') {
                  return `Purchased ${item.bundle_size?.toLocaleString()} SMS credits`;
                }
                if (item.change_type === 'subscription_cancelled') return 'Cancelled subscription';
                return 'Account activity';
              };
              
              return (
                <Card key={item.record_id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="text-3xl">{getIcon()}</div>
                      <div className="flex-1">
                        <p className="font-medium">{getDescription()}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-gray-500">
                            {new Date(item.changed_at * 1000).toLocaleDateString('en-US', { 
                              month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                          </span>
                          {item.changed_by && item.changed_by !== 'stripe_webhook' && (
                            <span className="text-xs text-gray-500">by {item.changed_by}</span>
                          )}
                          {item.changed_by === 'stripe_webhook' && (
                            <Badge variant="outline" className="text-xs">Stripe</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {(item.amount || item.price) && (
                          <div className="text-right">
                            <p className="font-bold">${(item.amount || item.price)?.toFixed(2)}</p>
                            <p className="text-xs text-gray-500">{item.currency || 'USD'}</p>
                          </div>
                        )}
                        {item.receipt_url && (
                          <Button variant="ghost" size="sm" onClick={() => window.open(item.receipt_url, '_blank')}>
                            Receipt
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {billingHistory.length === 0 && (
              <Card>
                <CardContent className="p-12 text-center">
                  <div className="text-gray-400 mb-2">
                    <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <p className="text-gray-500">No activity yet</p>
                  <p className="text-sm text-gray-400 mt-1">Your account history will appear here</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}